import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../App";

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/es_MX/appleid.auth.js";

function loadAppleScript() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const s = document.createElement("script");
    s.src = APPLE_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("No se pudo cargar el servicio de Apple"));
    document.head.appendChild(s);
  });
}

/**
 * "Continuar con Apple" button. Config-ready: it queries the backend for the
 * Apple Services ID. If Apple Sign-In isn't configured yet (no Apple
 * Developer credentials in the backend .env), the button shows a friendly
 * "coming soon" toast instead of breaking.
 */
export default function AppleSignInButton({ onSuccess }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/apple/config")
      .then((r) => mounted && setConfig(r.data))
      .catch(() => mounted && setConfig({ configured: false }));
    return () => {
      mounted = false;
    };
  }, []);

  const handleClick = async () => {
    if (!config?.configured) {
      toast.info(
        "Iniciar sesión con Apple estará disponible muy pronto. Mientras tanto, usa Google o tu correo.",
      );
      return;
    }
    setLoading(true);
    try {
      await loadAppleScript();
      window.AppleID.auth.init({
        clientId: config.client_id,
        scope: "name email",
        redirectURI: `${window.location.origin}/login`,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (!idToken) throw new Error("Apple no devolvió un token válido");
      const { data } = await api.post("/auth/apple", {
        id_token: idToken,
        first_name: res?.user?.name?.firstName || null,
        last_name: res?.user?.name?.lastName || null,
      });
      onSuccess?.(data);
    } catch (err) {
      // User closed the Apple popup — not an error worth surfacing
      if (err?.error === "popup_closed_by_user" || err?.error === "user_cancelled_authorize") {
        return;
      }
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No pudimos iniciar sesión con Apple. Intenta de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={loading || config === null}
      className="w-full h-11 bg-black hover:bg-neutral-800 text-white rounded-xl font-medium flex items-center justify-center gap-3"
      data-testid="apple-login-btn"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      )}
      Continuar con Apple
    </Button>
  );
}
